<template>
  <vz-dialog
    :name="name"
    :title="title"
    :hide-footer="waitingCode"
    small
    @showed="onShowed"
  >
    <vz-loader :status-loader="loader"/>
    <div style="display: none">{{ focused }}</div>
    <vz-form
      v-if="!success"
      v-model="isValid"
      ref="formRef"
    >
      <vz-row>
        <div class="position-relative w-100" v-on-click-outside="onBlur">
          <vz-phone-input
            ref="inputControl"
            v-model="phone"
            big
            required
            label="Телефон"
            :readonly="isReadonlyPhoneInput"
            :rules="rules"
          >
            <template #label>
              <slot name="phone-label"/>
            </template>
          </vz-phone-input>
          <div
            v-show="isFocus && customerPhones.length"
            ref="list"
            v-scroll="calculateListPosition"
            class="phonelist"
          >
            <div
              v-for="(phone, index) of customerPhones"
              :key="index"
              class="phonelist-item"
              @click="onPhoneClick(phone)"
              @touch="onPhoneClick(phone)"
            >
              {{ getMaskedPhone(phone) }}
            </div>
          </div>
        </div>
      </vz-row>

      <div v-if="otherCountryPhone" class="mt-15 pt-10">
        Для подтверждения оформления с использованием указанного номера
        необходимо перейти на
        <a :href="otherCountryPhone.link" @click.prevent="changeCountry">{{ otherCountryPhone.text }}</a>
        и продолжить оформление. Все введённые данные сохранятся.
      </div>

      <span v-if="!waitingCode && time > 0" class="text-body color-inactive">{{ timerLabel }}</span>
      <vz-row v-if="waitingCode">
        <div class="vz-input-new">
          <label for="vz-input-768" class="vz-input-new-label required">{{ confirmMessage }}</label>
        </div>
        <div class="flex flex-grow-1 flex-align-items-center flex-wrap">
          <div class="flex flex-grow-1 flex-align-items-center">
            <vz-input
              v-model="code"
              required
              style="min-width: 86px; max-width: 86px; width: 86px;"
              class="mr-10"
              placeholder="0000"
              :max-length="4"
              :rules="[formRules.required]"
            />
            <div
              class="flex flex-align-items-center"
              :class="{'vz-cursor-pointer': !isTimeCounter}"
              @click="refreshCode"
            >
              <span
                class="text-body"
                style="max-width: 164px;"
                :class="sendTitleColor"
              >
                {{ timerLabel }}
              </span>
              <span>
              <vz-icon
                name="refresh"
                variant="primary"
                size="30"
                :disabled="isTimeCounter"
              />
            </span>
            </div>
          </div>
        </div>
      </vz-row>
      <span v-if="errMsg" class="text-caption color-error">
        {{ errMsg }}
      </span>
    </vz-form>
    <div v-else>
      <div class="flex flex-align-items-center ml-20 mb-20">
        <vz-icon
          name="check-all"
          variant="success"
          size="24"
        />
        <vz-label without-dot variant="success" class="mb-0 ml-10">
          {{ successText }}
        </vz-label>
      </div>
    </div>
    <template
      #footer
    >
      <div class="flex flex-grow-1 flex-center flex-align-items-center" style="gap: 25px">
        <nuxt-link
          v-if="!waitingCode.value"
          :to="telegramBot"
          title="Telegram Bot"
          target="_blank"
        >
          <vz-icon
            url="/svg/telegram.svg"
            size="30"
            no-masked-icon
          />
        </nuxt-link>
        <vz-button
          v-if="otherCountryPhone"
          variant="primary"
          @click="changeCountry"
          big
        >
          Перейти
        </vz-button>
        <vz-button
          v-else
          variant="primary"
          :disabled="time > 0 || !isValid"
          @click="send"
          big
        >
          {{ btnTitle }}
        </vz-button>
      </div>
    </template>
  </vz-dialog>
</template>

<script setup lang="ts">
import formRules from '~/common/vz-form-rules';
import {Interval} from '~/types/types';
import {useOrderStore} from '~/stores/order';
import {getMaskedPhone, pricePayload} from '~/common/objects';
import {storeToRefs} from 'pinia';
import {orderSaveCalc} from '@/api/order';
import {useFocusWithin} from '@vueuse/core'
import {PropType} from 'vue';
import {getCountryByPhone, getFullLinkRedirect} from '~/common/functions';
import {useCityStore} from '~/stores/city';
import {telegramBot} from '~/api/constant-urls';
import VzIcon from '~/components/ui-kit/vz-icon.vue';
import VzButton from '~/components/ui-kit/vz-button.vue';
import {useUserStore} from '~/stores/personal/user';
import {confirmCode} from "~/api/user";

const emit = defineEmits(['code-successful'])

const props = defineProps({
  name: {
    type: String,
    default: 'confirm-phone'
  },
  title: {
    type: String,
    default: 'Подтверждение оформления'
  },
  successText: {
    type: String,
    default: 'Оформление успешно подтверждено'
  },
  customButtonTittle: {
    type: String,
    default: ''
  },
  readonlyPhone: {
    type: Boolean,
    default: null
  },
  needRedirect: {
    type: Boolean,
    default: false
  },
  phones: {
    type: Array,
    default: null
  },
  context: {
    type: String as PropType<'orderCreate' | 'orderUpdate' | 'orderCancel'>,
    default: 'orderCreate'
  }
})

const {$showToast, $showDialog, $hideDialog} = useNuxtApp()
const {orderForm} = storeToRefs(useOrderStore())
const {currentCity} = storeToRefs(useCityStore())
const {isAuth} = storeToRefs(useUserStore())
const route = useRoute()

const formRef = ref<any>(null)
const inputControl = ref<any>(null)
const list = ref<HTMLElement | null>(null)
const isValid = ref(false)
const {focused} = useFocusWithin(inputControl)
const phone = ref('')
const savePhone = ref('')
const confirmMessage = ref('Ожидайте код подтверждения через СМС или входящий звонок')
const loader = ref(false)
const waitingCode = ref(false)
const code = ref('')
const errMsg = ref('')
const time = ref(0)
const isFocus = ref(false)
const interval = ref<Interval | null>(null)
const success = ref(false)

watch(phone, async () => {
  formRef.value.validate(false)
})

const timerLabel = computed(() => {
  return time.value ? `Отправить код повторно через ${time.value} сек.` : 'Отправить код повторно'
})

const isTimeCounter = computed(() => {
  return time.value > 0
})

const sendTitleColor = computed(() => {
  return {
    'color-inactive': isTimeCounter.value,
    'color-primary': !isTimeCounter.value
  }
})

const isReadonlyPhoneInput = computed(() => {
  if (props.readonlyPhone !== null) {
    return props.readonlyPhone
  }
  return !!route.params.guid
})

const btnTitle = computed(() => {
  return props.customButtonTittle || (waitingCode.value ? 'Создать заказ' : 'Отправить код')
})

const rules = computed(() => {
  const result = [formRules.required, formRules.phone]
  if (props.needRedirect && !isAuth.value) {
    result.push(formRules.phoneCountry)
  }
  return result
})

const otherCountryPhone = computed(() => {
  if (isAuth.value) {
    return false
  }
  if (formRules.phone.cb(phone.value) === true && formRules.phoneCountry.cb(phone.value) !== true) {
    const country = getCountryByPhone(phone.value) || undefined
    const link = getFullLinkRedirect(
      {...currentCity.value, country, abbr: '', translit: ''},
      route.path,
      '',
      currentCity.value.abbr || currentCity.value.translit || ''
    )
    return {
      link,
      text: (new URL(`https:${link}`)).hostname
    }
  }
  return false
})

const customerPhones = computed(() => {
  if (props.phones !== null) {
    return props.phones
  }

  let phones: string[] = []
  let thirdPhones: string[] = []
  const listKeys = Object.values(orderForm.value.customer?.segregated?.list) || []

  const dispatchType = orderForm.value.customer.dispatch.type
  const destinationType = orderForm.value.customer.destination.type
  const dispatchPhones = orderForm.value.customer.dispatch[dispatchType].phone
  const destinationPhones = orderForm.value.customer.destination[destinationType].phone

  if (orderForm.value.customer.payer === 'third' || (orderForm.value.customer.payer === 'segregated' && listKeys.find(l => l === 'third'))) {
    const thirdType = orderForm.value.customer.third.type
    thirdPhones = orderForm.value.customer.third[thirdType].phone
  }
  phones = [...dispatchPhones, ...destinationPhones, ...thirdPhones]
  if (route.query.phoneNumber) {
    phones.push((+(route.query.phoneNumber || 0)).toString())
  }
  return phones.filter(function (item, pos) {
    return phones.indexOf(item) === pos
  })
})

async function onConfirmCode(phone: string) {
  const body = {
    recipient: phone,
    context: props.context,
    usingHtml: true
  }
  return confirmCode(body)
}

async function onShowed() {
  code.value = ''
  errMsg.value = ''
  success.value = false
  waitingCode.value = false
  phone.value = savePhone.value
  startTimer()
  setTimeout(() => {
    calculateListPosition()
  }, 0)
  await formRef.value.validate()
}

async function refreshCode() {
  if (isTimeCounter.value) {
    return
  }

  stopTimer()
  let data
  try {
    data = await onConfirmCode(`+${phone.value}`)
  } catch (e) {
    console.log(e)
  }

  if (!data) {
    loader.value = false
    return
  }
  time.value = data.timeout || 0
  confirmMessage.value = data.message || ''

  if (data.errors) {
    if (data.errors?.can_send_at.length) {
      $showToast({
        title: 'Ошибка',
        text: data.errors?.can_send_at[0] || data.errors?.can_send_at,
        variant: 'error'
      })
    } else {
      errMsg.value = 'Неверный код подтверждения. Пример кода: 1234'
    }
  } else {
    waitingCode.value = true
    code.value = ''
  }

  startTimer()
}

async function send(): Promise<void> {
  errMsg.value = ''
  const isValid = await formRef.value.validate(/*false*/)
  if (!isValid) {
    return
  }

  stopTimer()
  if (!waitingCode.value) {
    loader.value = true
    await refreshCode()
    loader.value = false
    return
  }

  savePhone.value = phone.value
  emit('code-successful', phone.value, code.value)
  time.value = 0
  success.value = true
  loader.value = false
  hide()
}

function onBlur() {
  isFocus.value = false
}

function onPhoneClick(value: string) {
  isFocus.value = false
  phone.value = value
}

function startTimer() {
  if (interval.value) {
    clearInterval(interval.value)
  }

  interval.value = setInterval(() => {
    time.value -= 1
    if (time.value <= 0) {
      stopTimer()
    }
  }, 1000)
}

function stopTimer() {
  time.value = 0
  if (interval.value) {
    clearInterval(interval.value)
  }
}

function calculateListPosition() {
  if (!list.value || !inputControl.value.$el) {
    return
  }

  const rect = list.value.getBoundingClientRect()
  const {top, left, height, width} = inputControl.value.$el.getBoundingClientRect()

  const fullHeight = top + height + rect.height
  list.value.style.left = `${left}px`
  list.value.style.width = `${width}px`

  if (fullHeight > window.innerHeight) {
    list.value.style.top = `${top - rect.height}px` // `-${rect.height - labelRect.height - 6}px`
    list.value.style.boxShadow = '2px -2px 4px rgba(0, 0, 0, 0.15)'
  } else {
    list.value.style.top = `${top + height}px`
    list.value.style.boxShadow = '0px 2px 4px rgba(0, 0, 0, 0.15)'
  }
}

async function changeCountry() {
  const protocol = process.env.NODE_ENV === 'development' ? 'http' : 'https'
  const {origin, hash} = await orderSaveCalc(pricePayload(orderForm.value))
  const newCountryLink = otherCountryPhone.value?.link || ''
  const url = new URL(`${protocol}://${newCountryLink}`)
  url.searchParams.set('calculation', hash)
  url.searchParams.set('currency', orderForm.value.currency)
  url.searchParams.set('set_city', currentCity.value.abbr || currentCity.value.translit || '')
  if (hash) {
    const link = url.toString()
    window.open(link, '_blank')
  }
}

function show() {
  $showDialog({name: props.name})
}

function hide() {
  $hideDialog({name: props.name})
}

watch(() => focused.value,
  (value) => {
    if (value) {
      isFocus.value = value
    }
  }
)

watch(
  code,
  (value, oldValue) => {
    if (value !== oldValue && code.value.length === 4) {
      send()
    }
  }
)

defineExpose({ waitingCode, show, phone })
</script>

<style lang="scss" scoped>
.phonelist {
  background: $white;
  border: 1px solid $border-gray;
  box-sizing: border-box;
  border-radius: 4px;
  padding: 10px 0;
  position: fixed;
  width: 100%;
  z-index: 100;

  &-item {
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 16px;
    padding: 10px 13px;

    &:hover {
      background-color: $component-background;
      cursor: pointer;
    }
  }
}
</style>
